import { QueryClientProvider } from '@tanstack/react-query';
import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './components/RequireAuth.js';
import { AppLayout } from './layouts/AppLayout.js';
import { AuthProvider } from './lib/auth.js';
import { queryClient } from './lib/query.js';
import { SessionQueryProvider } from './lib/sessionQueries.js';
import { ThemeProvider } from './lib/theme.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { InboxPage } from './pages/InboxPage.js';
import { LoginPage } from './pages/LoginPage.js';

// 常用入口保持直接加载，其余页面及 PDF/图表等依赖在首次访问时加载。
const AiLabelsPage = lazy(() => import('./pages/AiLabelsPage.js').then((m) => ({ default: m.AiLabelsPage })));
const AiPage = lazy(() => import('./pages/AiPage.js').then((m) => ({ default: m.AiPage })));
const AiQaPage = lazy(() => import('./pages/AiQaPage.js').then((m) => ({ default: m.AiQaPage })));
const AliasesPage = lazy(() => import('./pages/AliasesPage.js').then((m) => ({ default: m.AliasesPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.js').then((m) => ({ default: m.AnalyticsPage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage.js').then((m) => ({ default: m.ApiKeysPage })));
const AttachmentsPage = lazy(() => import('./pages/AttachmentsPage.js').then((m) => ({ default: m.AttachmentsPage })));
const FolderPage = lazy(() => import('./pages/FolderPage.js').then((m) => ({ default: m.FolderPage })));
const MessageDetailPage = lazy(() => import('./pages/MessageDetailPage.js').then((m) => ({ default: m.MessageDetailPage })));
const OutboundPage = lazy(() => import('./pages/OutboundPage.js').then((m) => ({ default: m.OutboundPage })));
const RulesPage = lazy(() => import('./pages/RulesPage.js').then((m) => ({ default: m.RulesPage })));
const SearchPage = lazy(() => import('./pages/SearchPage.js').then((m) => ({ default: m.SearchPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const SourceAccountsPage = lazy(() => import('./pages/SourceAccountsPage.js').then((m) => ({ default: m.SourceAccountsPage })));
const TemporaryPage = lazy(() => import('./pages/TemporaryPage.js').then((m) => ({ default: m.TemporaryPage })));
const TrashPage = lazy(() => import('./pages/TrashPage.js').then((m) => ({ default: m.TrashPage })));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SessionQueryProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <AppLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="aliases" element={<AliasesPage />} />
                  <Route path="inbox" element={<InboxPage />} />
                  <Route path="inbox/:id" element={<MessageDetailPage />} />
                  <Route path="folders/:folderId" element={<FolderPage />} />
                  <Route path="rules" element={<RulesPage />} />
                  {/* F67：旧「转发设置」独立页 → 重定向到规则页的转发目的地 tab（书签兼容） */}
                  <Route
                    path="forwards"
                    element={<Navigate to="/app/rules?tab=forwards" replace />}
                  />
                  <Route path="temporary" element={<TemporaryPage />} />
                  <Route path="attachments" element={<AttachmentsPage />} />
                  <Route path="trash" element={<TrashPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="ai" element={<AiPage />} />
                  <Route path="ai-labels" element={<AiLabelsPage />} />
                  <Route path="ai-qa" element={<AiQaPage />} />
                  <Route path="outbound" element={<OutboundPage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="api-keys" element={<ApiKeysPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="source-accounts" element={<SourceAccountsPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Routes>
            </BrowserRouter>
          </SessionQueryProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
